import { useState } from 'react';
import { App, Button, Modal, Space, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { api } from '../../api/client';
import { StatsRangeFilter, useStatsRange } from './ListenerStatsWidgets';
import { downloadOnlineWorkbook, type OnlineExport } from './onlineExport';

/**
 * "Export Excel" on the Listeners page: every listener in the current tab +
 * search, as an .xlsx with a Listener details sheet (all the table's columns
 * and more), a day-wise online Summary and one sheet per listener. Its own date range (default: last 7 days), because
 * the page's range is usually "Today" and a day-wise export wants several days.
 */
export function OnlineExportButton({
  filter,
  scopeLabel,
  searched,
}: {
  /** The list's current filter (`status` / `dummy` / `q`). */
  filter: Record<string, string | undefined>;
  scopeLabel: string;
  searched: boolean;
}) {
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { preset, setPreset, setCustom, range } = useStatsRange('last7');

  async function run() {
    if (!range) return;
    setBusy(true);
    try {
      const { data } = await api.get<OnlineExport>('/admin/listeners/online-export', {
        params: { ...filter, ...range },
      });
      if (data.listeners.length === 0) {
        message.info('No listeners match this filter');
        return;
      }
      const first = data.days[0] ?? 'range';
      const last = data.days.at(-1) ?? first;
      await downloadOnlineWorkbook(data, `listeners_${first}_to_${last}.xlsx`);
      message.success(`Exported ${data.listeners.length} listeners`);
      setOpen(false);
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      message.error(msg ?? 'Could not export the listeners');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button icon={<DownloadOutlined />} onClick={() => setOpen(true)}>
        Export Excel
      </Button>
      <Modal
        title="Export listeners"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => void run()}
        okText="Download .xlsx"
        okButtonProps={{ loading: busy, disabled: !range }}
        destroyOnClose
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Typography.Text>
            Full details of <b>{scopeLabel}</b>
            {searched ? ' (current search)' : ''}: a Listener details sheet (phone, login, online, calls and
            earnings for the range), a day-wise online Summary, and one sheet per listener with every online
            session.
          </Typography.Text>
          <StatsRangeFilter preset={preset} setPreset={setPreset} setCustom={setCustom} />
          <Typography.Text type="secondary">Up to 92 days. Days follow your browser&apos;s time zone.</Typography.Text>
        </Space>
      </Modal>
    </>
  );
}
